import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AuthService } from '../../../core/auth/auth.service';
import { AuthErrorResponse } from '../../../core/auth/auth.types';
import { IconComponent } from '../../../shared/icon/icon';

type LoginErrorState =
  | 'none'
  | 'EMAIL_NOT_FOUND'
  | 'INVALID_CREDENTIALS'
  | 'EMAIL_NOT_VERIFIED'
  | 'ACCOUNT_DISABLED'
  | 'TOTP_INVALID'
  | 'UNKNOWN';

@Component({
  selector: 'app-login',
  imports: [
    IconComponent,
    RouterLink,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './login.html',
  styleUrl: './login.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);

  protected readonly showPassword = signal(false);
  protected readonly isLoading = signal(false);
  protected readonly errorState = signal<LoginErrorState>('none');
  /** Flips to true once the server asks for a second factor for this account. */
  protected readonly totpRequired = signal(false);

  protected readonly form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
    totpCode: [''],
  });

  protected get emailControl() {
    return this.form.controls.email;
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.isLoading() || this.form.invalid) return;

    this.errorState.set('none');
    this.isLoading.set(true);

    const { email, password, totpCode } = this.form.getRawValue();

    this.authService.login(email!, password!, totpCode || undefined).subscribe({
      next: () => {
        this.isLoading.set(false);
        if (this.authService.profileCompleted()) {
          void this.router.navigate(['/dashboard']);
        } else {
          void this.router.navigate(['/auth/complete-profile']);
        }
      },
      error: (err: unknown) => {
        this.isLoading.set(false);
        if (err instanceof HttpErrorResponse) {
          const authErr = this.authService.getAuthError(err);
          // A second-factor prompt isn't a failure — reveal the code field and
          // wait for the user to enter it, keeping email/password intact.
          if (authErr?.code === 'TOTP_REQUIRED') {
            this.totpRequired.set(true);
            this.errorState.set('none');
            return;
          }
          this.errorState.set(this.mapErrorCode(authErr));
        } else {
          this.errorState.set('UNKNOWN');
        }
      },
    });
  }

  protected onResendVerification(): void {
    const email = this.emailControl.value;
    if (!email) return;
    // Email is passed via in-memory navigation state only — never as a URL query param.
    // URLs leak to access logs, browser history, Referer headers, and analytics.
    void this.router.navigate(['/auth/check-email'], { state: { email } });
  }

  private mapErrorCode(authErr: AuthErrorResponse | null): LoginErrorState {
    if (!authErr) return 'UNKNOWN';
    switch (authErr.code) {
      case 'EMAIL_NOT_FOUND':
        return 'EMAIL_NOT_FOUND';
      case 'INVALID_CREDENTIALS':
        return 'INVALID_CREDENTIALS';
      case 'EMAIL_NOT_VERIFIED':
        return 'EMAIL_NOT_VERIFIED';
      case 'ACCOUNT_DISABLED':
        return 'ACCOUNT_DISABLED';
      case 'TOTP_INVALID':
        return 'TOTP_INVALID';
      default:
        return 'UNKNOWN';
    }
  }
}
